import React, { useState } from 'react';
import { motion } from 'framer-motion';

const BasicRegistrationForm = ({ onClose, isOpen }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    nationality: '',
    countryOfResidence: '',
    email: '',
    phoneNumber: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['fullName', 'dateOfBirth', 'nationality', 'countryOfResidence', 'email', 'phoneNumber'];
    return requiredFields.every(field => formData[field].trim() !== '');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ''));
  };

  const validateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 18;
    }
    return age >= 18;
  };

  const submitApplication = async (applicationData) => {
    const response = await fetch('https://api.blockfirstcapital.com/api/submit-application', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(applicationData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    if (!validateEmail(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    if (!validatePhone(formData.phoneNumber)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid phone number.' });
      return;
    }

    if (!validateAge(formData.dateOfBirth)) {
      setSubmitStatus({ type: 'error', message: 'You must be at least 18 years old to register.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const applicationData = {
        name: formData.fullName,
        date_of_birth: formData.dateOfBirth,
        nationality: formData.nationality,
        country_of_residence: formData.countryOfResidence,
        email: formData.email,
        phone: formData.phoneNumber,
        application_type: 'basic_application',
        status: 'pending'
      };

      await submitApplication(applicationData);

      setSubmitStatus({
        type: 'success',
        message: 'Application submitted successfully! Please check your email for next steps.'
      });

      setTimeout(() => {
        setFormData({
          fullName: '',
          dateOfBirth: '',
          nationality: '',
          countryOfResidence: '',
          email: '',
          phoneNumber: ''
        });
        setSubmitStatus(null);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitStatus({ type: 'error', message: 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col my-6"
        style={{ 
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className="bg-gradient-to-r from-[#f59e0b] to-[#fbbf24] text-[#111827] p-6 rounded-t-2xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Start Your Application</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-[#111827]/80 hover:text-[#111827] transition-colors text-3xl leading-none cursor-pointer touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Close form"
            >
              ×
            </button>
          </div>
          <p className="text-[#111827]/80 mt-2">
            Complete this form to begin your journey with Blockfirst Capital.
          </p>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Legal Name *
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base"
              placeholder="Enter your full legal name"
              required
              style={{ WebkitAppearance: 'none' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth *
            </label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base"
              required
              style={{ WebkitAppearance: 'none' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nationality *
            </label>
            <select
              value={formData.nationality}
              onChange={(e) => handleInputChange('nationality', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base cursor-pointer"
              required
              style={{ WebkitAppearance: 'none' }}
            >
              <option value="">Select your nationality</option>
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="NL">Netherlands</option>
              <option value="BE">Belgium</option>
              <option value="CH">Switzerland</option>
              <option value="AT">Austria</option>
              <option value="SE">Sweden</option>
              <option value="NO">Norway</option>
              <option value="DK">Denmark</option>
              <option value="FI">Finland</option>
              <option value="JP">Japan</option>
              <option value="SG">Singapore</option>
              <option value="HK">Hong Kong</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country of Residence *
            </label>
            <select
              value={formData.countryOfResidence}
              onChange={(e) => handleInputChange('countryOfResidence', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base cursor-pointer"
              required
              style={{ WebkitAppearance: 'none' }}
            >
              <option value="">Select your country of residence</option>
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="IT">Italy</option>
              <option value="ES">Spain</option>
              <option value="NL">Netherlands</option>
              <option value="BE">Belgium</option>
              <option value="CH">Switzerland</option>
              <option value="AT">Austria</option>
              <option value="SE">Sweden</option>
              <option value="NO">Norway</option>
              <option value="DK">Denmark</option>
              <option value="FI">Finland</option>
              <option value="JP">Japan</option>
              <option value="SG">Singapore</option>
              <option value="HK">Hong Kong</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base"
              placeholder="Enter your email address"
              required
              style={{ WebkitAppearance: 'none' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fbbf24] focus:border-transparent transition-colors text-base"
              placeholder="Enter your phone number"
              required
              style={{ WebkitAppearance: 'none' }}
            />
          </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#22c55e] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#16a34a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer touch-manipulation min-w-[140px]"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <span className="mr-2 inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Submitting...
                  </span>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </form>

          {submitStatus && (
            <div className="px-6 pb-6 text-center flex-shrink-0">
              {submitStatus.type === 'success' && (
                <div className="bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-lg relative" role="alert">
                  <strong className="font-bold">Success!</strong>
                  <span className="block sm:inline"> {submitStatus.message}</span>
                </div>
              )}
              {submitStatus.type === 'error' && (
                <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-lg relative" role="alert">
                  <strong className="font-bold">Error!</strong>
                  <span className="block sm:inline"> {submitStatus.message}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BasicRegistrationForm;
